import { computed, makeObservable, observable } from "mobx"
import { Translation } from "models/faq.model"
import { htmlToTextTransform } from "@lib/html"
import { PostElementBackground } from "@serializers/article"
import { config } from "@lib/config"

import { BaseModel } from "./base.model"

const defaultBundleInfo: BundleInfo = {
  bundle_name: "",
  bundle_name_cypix: "",
  bundle_name_paythrone: "",
  bundle_name_mobi: "",
  text: "",
}

function pluralize(count: number, words: string[]) {
  const cases = [2, 0, 1, 1, 1, 2]
  return `${count} ${
    words[
      count % 100 > 4 && count % 100 < 20 ? 2 : cases[Math.min(count % 10, 5)]
    ]
  }`
}

export class AndroidPaymentsModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  @observable translations = [] as Translation[]
  @observable isPaymentEnabled = true as boolean
  @observable subscriptionBlocks = [] as Block[]
  @observable inappBlocks = [] as Block[]
  @observable femalePremiumSubscriptionBlocks = [] as Block[]

  @observable subscriptionWeek: BundleInfo = defaultBundleInfo
  @observable subscriptionMonth: BundleInfo = defaultBundleInfo
  @observable subscriptionYear: BundleInfo = defaultBundleInfo
  @observable koth1: BundleInfo = defaultBundleInfo
  @observable koth5: BundleInfo = defaultBundleInfo
  @observable koth15: BundleInfo = defaultBundleInfo
  @observable koth30: BundleInfo = defaultBundleInfo
  @observable koth50: BundleInfo = defaultBundleInfo
  @observable instantChat1: BundleInfo = defaultBundleInfo
  @observable instantChat5: BundleInfo = defaultBundleInfo
  @observable instantChat15: BundleInfo = defaultBundleInfo
  @observable instantChat30: BundleInfo = defaultBundleInfo
  @observable instantChat50: BundleInfo = defaultBundleInfo
  @observable gift1: BundleInfo = defaultBundleInfo
  @observable gift5: BundleInfo = defaultBundleInfo
  @observable gift15: BundleInfo = defaultBundleInfo
  @observable gift30: BundleInfo = defaultBundleInfo
  @observable gift50: BundleInfo = defaultBundleInfo
  @observable chips5: BundleInfo = defaultBundleInfo
  @observable chips10: BundleInfo = defaultBundleInfo
  @observable chips15: BundleInfo = defaultBundleInfo
  @observable chips30: BundleInfo = defaultBundleInfo
  @observable chips50: BundleInfo = defaultBundleInfo
  @observable incognito1: BundleInfo = defaultBundleInfo
  @observable incognito3: BundleInfo = defaultBundleInfo
  @observable incognito7: BundleInfo = defaultBundleInfo
  @observable femalePremiumSubscriptionWeek: BundleInfo = defaultBundleInfo
  @observable femalePremiumSubscriptionMonth: BundleInfo = defaultBundleInfo
  @observable femalePremiumSubscriptionYear: BundleInfo = defaultBundleInfo

  @observable product?: ProductQueryUnion = undefined
  @observable bundleName?: string

  @observable externalBundleNames?: string[]

  @observable apiKey: string = config.data.pureApiKey!
  @observable mixedBundlesApiKey: string = config.data.pureApiKey!

  @observable bundles: { [bundleName: string]: Bundle } = {}
  @observable currentTimestamp = (Date.now() / 1000) as number
  @observable backUrl = "" as string
  @observable backUrlFail = "" as string
  @observable store: PaymentStore = "pay2me"

  @computed get subscriptionBundlesInfo(): BundleInfo[] {
    return [
      this.subscriptionWeek,
      this.subscriptionMonth,
      this.subscriptionYear,
    ]
  }

  @computed get femalePremiumSubscriptionsBundlesInfo(): BundleInfo[] {
    return [
      this.femalePremiumSubscriptionWeek,
      this.femalePremiumSubscriptionMonth,
      this.femalePremiumSubscriptionYear,
    ]
  }

  @computed get externalBundlesInfo(): BundleInfo[] {
    if (!this.externalBundleNames) return []
    return this.externalBundleNames.map((bundle) => ({
      bundle_name: bundle,
      bundle_name_cypix: bundle,
      bundle_name_paythrone: bundle,
      bundle_name_mobi: bundle,
      text: this.generateExternalBundleText(bundle),
    }))
  }

  generateExternalBundleText = (bundleName: string) => {
    let quantity = 1

    // eslint-disable-next-line
    if (this.product && this.bundles[bundleName]?.products?.[this.product]) {
      // eslint-disable-next-line
      quantity = this.bundles[bundleName]?.products?.[this.product!]!
    } else {
      try {
        if (bundleName.includes("bundle")) {
          const extractedQuantity = bundleName
            .split(".")
            .find((part) => part.includes("bundle"))
            ?.match(/\d+/)?.[0]

          if (extractedQuantity && !isNaN(Number(extractedQuantity))) {
            quantity = Number(extractedQuantity)
          }
        }
      } catch {}
    }

    return pluralize(quantity, [
      this.getTemplate(`${this.product}.one`),
      this.getTemplate(`${this.product}.few`),
      this.getTemplate(`${this.product}.many`),
    ])
  }

  getKothBundlesInfo = (oldVersion: boolean, quantities?: string[]) => {
    if (quantities && quantities.length) {
      return this.getBundleInfoByQuantities("koth", quantities)
    }
    if (oldVersion) {
      return [this.koth1, this.koth5]
    }
    return [this.koth1, this.koth5, this.koth30, this.koth50]
  }

  getGiftBundlesInfo = (
    oldVersion: boolean,
    extended: boolean,
    quantities?: string[],
  ) => {
    if (quantities && quantities.length) {
      return this.getBundleInfoByQuantities("gift", quantities)
    }
    if (oldVersion) {
      return [this.gift1, this.gift5]
    }
    if (!extended) {
      return [this.gift1, this.gift5, this.gift30]
    }
    return [this.gift1, this.gift5, this.gift30, this.gift50]
  }

  getICBundlesInfo = (oldVersion: boolean, quantities?: string[]) => {
    if (quantities && quantities.length) {
      return this.getBundleInfoByQuantities("instantChat", quantities)
    }
    if (oldVersion) {
      return [this.instantChat1, this.instantChat5]
    }
    return [
      this.instantChat1,
      this.instantChat5,
      this.instantChat30,
      this.instantChat50,
    ]
  }

  getChipsBundlesInfo = (oldVersion: boolean, quantities?: string[]) => {
    if (quantities && quantities.length) {
      return this.getBundleInfoByQuantities("chips", quantities)
    }

    if (oldVersion) {
      return [this.chips5, this.chips10]
    }
    return [this.chips5, this.chips10, this.chips30, this.chips50]
  }

  getIncognitoBundlesInfo = (quantities?: string[]) => {
    if (quantities && quantities.length) {
      return this.getBundleInfoByQuantities("incognito", quantities)
    }
    return [this.incognito1, this.incognito3, this.incognito7]
  }

  @computed get mixedBundlesInfo(): BundleInfo[] {
    if (!this.bundleName) {
      return []
    } else {
      return [
        {
          bundle_name: this.bundleName,
          text: "КУПИТЬ СЕТ",
          bundle_name_cypix: this.bundleName,
          bundle_name_paythrone: this.bundleName,
          bundle_name_mobi: this.bundleName,
        },
      ]
    }
  }

  getBundlesByQuantities = (product: string, quantities: string[]) => {
    return this.getBundles(this.getBundleInfoByQuantities(product, quantities))
  }

  getBundleInfoByQuantities = (product: string, quantities: string[]) => {
    return quantities.map((i) => this[`${product}${i}`]).filter((i) => !!i)
  }

  getBundlesByProduct = (
    product: ProductQueryUnion,
    oldVersion: boolean = true,
    extended: boolean = true,
    quantities?: string[],
  ) => {
    switch (product) {
      case "subscriptions":
        return this.getBundles(this.subscriptionBundlesInfo)
      case "female_premium_subscriptions":
        return this.getBundles(this.femalePremiumSubscriptionsBundlesInfo)
      case "instant_chat":
        return this.getBundles(this.getICBundlesInfo(oldVersion, quantities))
      case "gift":
        return this.getBundles(
          this.getGiftBundlesInfo(oldVersion, extended, quantities),
        )
      case "chips":
        return this.getBundles(this.getChipsBundlesInfo(oldVersion, quantities))
      case "koth":
        return this.getBundles(this.getKothBundlesInfo(oldVersion, quantities))
      case "incognito":
        return this.getBundles(this.getIncognitoBundlesInfo(quantities))
      case "mixed_bundle":
        return this.getBundles(this.mixedBundlesInfo)
      default:
        return []
    }
  }

  getBundlesInfo = (
    product: ProductQueryUnion,
    oldVersion: boolean = true,
    extended: boolean = true,
    quantities?: string[],
  ) => {
    switch (product) {
      case "subscriptions":
        return this.subscriptionBundlesInfo
      case "female_premium_subscriptions":
        return this.femalePremiumSubscriptionsBundlesInfo
      case "instant_chat":
        return this.getICBundlesInfo(oldVersion, quantities)
      case "gift":
        return this.getGiftBundlesInfo(oldVersion, extended, quantities)
      case "chips":
        return this.getChipsBundlesInfo(oldVersion, quantities)
      case "koth":
        return this.getKothBundlesInfo(oldVersion, quantities)
      case "incognito":
        return this.getIncognitoBundlesInfo(quantities)
      case "mixed_bundle":
        return this.mixedBundlesInfo
      default:
        return []
    }
  }

  getBundles = (bundles: BundleInfo[]) => {
    return bundles
      .map(
        (bundle) =>
          this.bundles[
            this.store === "pay2me"
              ? bundle.bundle_name
              : bundle[`bundle_name_${this.store}`]
          ],
      )
      .filter((i) => Boolean(i))
  }

  fetchAndroidPaymentsPage = async (
    product?: ProductQueryUnion,
    bundleName?: string,
    redirectUrl?: string,
    oldVersion: boolean = true,
    extended: boolean = true,
    quantities?: string[],
    redirectUrlFail?: string,
    store: string = "pay2me",
    bundles?: string[],
  ) => {
    try {
      const {
        translations,
        is_payments_enabled: isPaymentEnabled,
        subscription_blocks: subscriptionBlocks,
        inapp_blocks: inappBlocks,
        female_premium_blocks: femalePremiumSubscriptionBlocks,
        subscription_week: subscriptionWeek,
        subscription_month: subscriptionMonth,
        subscription_year: subscriptionYear,
        female_premium_subscription_week: femalePremiumSubscriptionWeek,
        female_premium_subscription_month: femalePremiumSubscriptionMonth,
        female_premium_subscription_year: femalePremiumSubscriptionYear,
        koth_1: koth1,
        koth_5: koth5,
        koth_15: koth15,
        koth_30: koth30,
        koth_50: koth50,
        instant_chat_1: instantChat1,
        instant_chat_5: instantChat5,
        instant_chat_15: instantChat15,
        instant_chat_30: instantChat30,
        instant_chat_50: instantChat50,
        gift_1: gift1,
        gift_5: gift5,
        gift_15: gift15,
        gift_30: gift30,
        gift_50: gift50,
        chips_5: chips5,
        chips_10: chips10,
        chips_15: chips15,
        chips_30: chips30,
        chips_50: chips50,
        incognito_1: incognito1,
        incognito_3: incognito3,
        incognito_7: incognito7,
        payment_back_url: backUrl,
        api_key: apiKey,
        mixed_bundles_api_key: mixedBundlesApiKey,
      } = await this.Providers.AndroidPaymentsProvider.find()

      this.applyData({
        translations,
        isPaymentEnabled,
        subscriptionBlocks,
        inappBlocks,
        femalePremiumSubscriptionBlocks,
        backUrl: redirectUrl ?? backUrl,
        backUrlFail: redirectUrlFail ?? redirectUrl ?? backUrl,
        subscriptionWeek,
        subscriptionMonth,
        subscriptionYear,
        femalePremiumSubscriptionWeek,
        femalePremiumSubscriptionMonth,
        femalePremiumSubscriptionYear,
        koth1,
        koth5,
        koth15,
        koth30,
        koth50,
        instantChat1,
        instantChat5,
        instantChat15,
        instantChat30,
        instantChat50,
        gift1,
        gift5,
        gift15,
        gift30,
        gift50,
        chips5,
        chips10,
        chips15,
        chips30,
        chips50,
        incognito1,
        incognito3,
        incognito7,
        apiKey,
        mixedBundlesApiKey,
        store,
        externalBundleNames: bundles,
      })

      if (bundleName) {
        this.applyData({
          bundleName,
        })
      }

      if (product) {
        this.applyData({
          product,
        })

        if (bundles?.length) {
          await this.fetchBundles(bundles)
        } else {
          switch (product) {
            case "subscriptions":
              await this.fetchSubscriptionBundles()
              break
            case "female_premium_subscriptions":
              await this.fetchFemalePremiumSubscriptionBundles()
              break
            case "koth":
              await this.fetchKothBundles(oldVersion, quantities)
              break
            case "instant_chat":
              await this.fetchInstantChatBundles(oldVersion, quantities)
              break
            case "gift":
              await this.fetchGiftBundles(oldVersion, extended, quantities)
              break
            case "chips":
              await this.fetchChipsBundles(oldVersion, quantities)
              break
            case "incognito":
              await this.fetchIncognitoBundles(quantities)
              break
            case "mixed_bundle":
              if (bundleName) {
                await this.fetchBundles([bundleName], true)
              }
              break
            default:
              break
          }
        }
      }
    } catch {}
  }

  fetchBundles = async (bundleNames: string[], isMixedBundles?: boolean) => {
    try {
      const {
        results,
      } = await this.Providers.AndroidPaymentsProvider.getBundles(
        bundleNames,
        isMixedBundles ? this.mixedBundlesApiKey : this.apiKey,
        this.store,
      )

      this.addBundlesToDictionary(results)
      this.applyData({
        currentTimestamp: Date.now() / 1000,
      })
    } catch (e) {}
  }

  addBundlesToDictionary = (payload: Bundle[]) => {
    const convertedBundles = {}
    payload.forEach((bundle) => (convertedBundles[bundle.name] = bundle))
    this.applyData({
      bundles: {
        ...this.bundles,
        ...convertedBundles,
      },
    })
  }

  fetchSubscriptionBundles = async () => {
    try {
      await this.fetchBundles(
        this.subscriptionBundlesInfo.map((i) =>
          this.store === "pay2me"
            ? i.bundle_name
            : i[`bundle_name_${this.store}`],
        ),
      )
    } catch {}
  }

  fetchFemalePremiumSubscriptionBundles = async () => {
    try {
      await this.fetchBundles(
        this.femalePremiumSubscriptionsBundlesInfo.map((i) =>
          this.store === "pay2me"
            ? i.bundle_name
            : i[`bundle_name_${this.store}`],
        ),
      )
    } catch {}
  }

  fetchKothBundles = async (oldVersion: boolean, quantities?: string[]) => {
    try {
      await this.fetchBundles(
        this.getKothBundlesInfo(oldVersion, quantities).map((i) =>
          this.store === "cypix" ? i.bundle_name_cypix : i.bundle_name,
        ),
        false,
      )
    } catch {}
  }

  fetchInstantChatBundles = async (
    oldVersion: boolean,
    quantities?: string[],
  ) => {
    try {
      await this.fetchBundles(
        this.getICBundlesInfo(oldVersion, quantities).map((i) =>
          this.store === "cypix" ? i.bundle_name_cypix : i.bundle_name,
        ),
        false,
      )
    } catch {}
  }

  fetchGiftBundles = async (
    oldVersion: boolean,
    extended: boolean,
    quantities?: string[],
  ) => {
    try {
      await this.fetchBundles(
        this.getGiftBundlesInfo(oldVersion, extended, quantities).map((i) =>
          this.store === "cypix" ? i.bundle_name_cypix : i.bundle_name,
        ),
        false,
      )
    } catch {}
  }

  fetchChipsBundles = async (oldVersion: boolean, quantities?: string[]) => {
    try {
      await this.fetchBundles(
        this.getChipsBundlesInfo(oldVersion, quantities).map((i) =>
          this.store === "cypix" ? i.bundle_name_cypix : i.bundle_name,
        ),
        false,
      )
    } catch {}
  }

  fetchIncognitoBundles = async (quantities?: string[]) => {
    try {
      await this.fetchBundles(
        this.getIncognitoBundlesInfo(quantities).map((i) =>
          this.store === "cypix" ? i.bundle_name_cypix : i.bundle_name,
        ),
        false,
      )
    } catch {}
  }

  getPaymentUrl = async (bundleName: string, userId: string) => {
    if (!bundleName || !userId) {
      return
    }

    const isMixedBundles = bundleName === this.bundleName

    try {
      const {
        redirect_url,
      } = await this.Providers.AndroidPaymentsProvider.getPaymentUrl(
        bundleName,
        userId,
        this.backUrl,
        isMixedBundles ? this.mixedBundlesApiKey : this.apiKey,
        "Pure",
        this.store,
        this.backUrlFail,
      )
      return redirect_url
    } catch {}
  }

  getTemplate = (key: string, simpleText = false) => {
    const find = this.translations.find((template) => template.key === key)
    if (find) {
      return simpleText ? htmlToTextTransform(find.value) : find.value
    }
    return ""
  }

  hydrate = (data: unknown) => {
    // @ts-ignore
    this.applyData({ translations: data?.translations })
    // @ts-ignore
    this.applyData({ isPaymentEnabled: data?.isPaymentEnabled })
    // @ts-ignore
    this.applyData({ subscriptionBlocks: data?.subscriptionBlocks })
    // @ts-ignore
    this.applyData({ inappBlocks: data?.inappBlocks })
    this.applyData({
      // @ts-ignore
      femalePremiumSubscriptionBlocks: data?.femalePremiumSubscriptionBlocks,
    })
    // @ts-ignore
    this.applyData({ product: data?.product })
    // @ts-ignore
    this.applyData({ subscriptionWeek: data?.subscriptionWeek })
    // @ts-ignore
    this.applyData({ subscriptionMonth: data?.subscriptionMonth })
    // @ts-ignore
    this.applyData({ subscriptionYear: data?.subscriptionYear })
    this.applyData({
      // @ts-ignore
      femalePremiumSubscriptionWeek: data?.femalePremiumSubscriptionWeek,
    })
    this.applyData({
      // @ts-ignore
      femalePremiumSubscriptionMonth: data?.femalePremiumSubscriptionMonth,
    })
    this.applyData({
      // @ts-ignore
      femalePremiumSubscriptionYear: data?.femalePremiumSubscriptionYear,
    })
    // @ts-ignore
    this.applyData({ koth1: data?.koth1 })
    // @ts-ignore
    this.applyData({ koth5: data?.koth5 })
    // @ts-ignore
    this.applyData({ koth15: data?.koth15 })
    // @ts-ignore
    this.applyData({ koth30: data?.koth30 })
    // @ts-ignore
    this.applyData({ koth50: data?.koth50 })
    // @ts-ignore
    this.applyData({ instantChat1: data?.instantChat1 })
    // @ts-ignore
    this.applyData({ instantChat5: data?.instantChat5 })
    // @ts-ignore
    this.applyData({ instantChat15: data?.instantChat15 })
    // @ts-ignore
    this.applyData({ instantChat30: data?.instantChat30 })
    // @ts-ignore
    this.applyData({ instantChat50: data?.instantChat50 })
    // @ts-ignore
    this.applyData({ gift1: data?.gift1 })
    // @ts-ignore
    this.applyData({ gift5: data?.gift5 })
    // @ts-ignore
    this.applyData({ gift15: data?.gift15 })
    // @ts-ignore
    this.applyData({ gift50: data?.gift50 })
    // @ts-ignore
    this.applyData({ gift30: data?.gift30 })
    // @ts-ignore
    this.applyData({ chips5: data?.chips5 })
    // @ts-ignore
    this.applyData({ chips10: data?.chips10 })
    // @ts-ignore
    this.applyData({ chips15: data?.chips15 })
    // @ts-ignore
    this.applyData({ chips30: data?.chips30 })
    // @ts-ignore
    this.applyData({ chips50: data?.chips50 })
    // @ts-ignore
    this.applyData({ incognito1: data?.incognito1 })
    // @ts-ignore
    this.applyData({ incognito3: data?.incognito3 })
    // @ts-ignore
    this.applyData({ incognito7: data?.incognito7 })
    // @ts-ignore
    this.applyData({ backUrl: data?.backUrl })
    // @ts-ignore
    this.applyData({ backUrlFail: data?.backUrlFail })
    // @ts-ignore
    this.applyData({ bundles: data?.bundles })
    // @ts-ignore
    this.applyData({ apiKey: data?.apiKey })
    // @ts-ignore
    this.applyData({ mixedBundlesApiKey: data?.mixedBundlesApiKey })
    // @ts-ignore
    this.applyData({ bundleName: data?.bundleName })
    // @ts-ignore
    this.applyData({ store: data?.store })
    // @ts-ignore
    this.applyData({ externalBundleNames: data?.externalBundleNames })
  }

  serialize = () => {
    return {
      translations: this.translations,
      isPaymentEnabled: this.isPaymentEnabled,
      subscriptionBlocks: this.subscriptionBlocks,
      inappBlocks: this.inappBlocks,
      femalePremiumSubscriptionBlocks: this.femalePremiumSubscriptionBlocks,
      product: this.product,
      backUrl: this.backUrl,
      backUrlFail: this.backUrlFail,
      subscriptionWeek: this.subscriptionWeek,
      subscriptionMonth: this.subscriptionMonth,
      subscriptionYear: this.subscriptionYear,
      femalePremiumSubscriptionWeek: this.femalePremiumSubscriptionWeek,
      femalePremiumSubscriptionMonth: this.femalePremiumSubscriptionMonth,
      femalePremiumSubscriptionYear: this.femalePremiumSubscriptionYear,
      koth1: this.koth1,
      koth5: this.koth5,
      koth15: this.koth15,
      koth30: this.koth30,
      koth50: this.koth50,
      instantChat1: this.instantChat1,
      instantChat5: this.instantChat5,
      instantChat15: this.instantChat15,
      instantChat30: this.instantChat30,
      instantChat50: this.instantChat50,
      gift1: this.gift1,
      gift5: this.gift5,
      gift15: this.gift15,
      gift30: this.gift30,
      gift50: this.gift50,
      chips5: this.chips5,
      chips10: this.chips10,
      chips15: this.chips15,
      chips30: this.chips30,
      chips50: this.chips50,
      incognito1: this.incognito1,
      incognito3: this.incognito3,
      incognito7: this.incognito7,
      bundles: this.bundles,
      apiKey: this.apiKey,
      mixedBundlesApiKey: this.mixedBundlesApiKey,
      bundleName: this.bundleName,
      store: this.store,
      externalBundleNames: this.externalBundleNames,
    }
  }
}

export interface Block {
  background: PostElementBackground
  Content: string
}

export interface BundleInfo {
  bundle_name: string
  bundle_name_cypix: string
  bundle_name_paythrone: string
  bundle_name_mobi: string
  text: string
}

export interface Bundle {
  currency: "RUB"
  description: string
  has_trial: boolean
  name: string
  price: number
  price_trial: number
  products?: Record<InAppProductTypeUnion, number>
}

export type InAppProductTypeUnion =
  | "koth"
  | "instant_chat"
  | "gift"
  | "chips"
  | "incognito"

export type ProductQueryUnion =
  | "subscriptions"
  | "koth"
  | "instant_chat"
  | "gift"
  | "chips"
  | "mixed_bundle"
  | "incognito"
  | "female_premium_subscriptions"

export type PaymentStore = "pay2me" | "cypix" | "paythrone" | "mobi"
